/**
 * keyUtils.js
 * Common cryptographic helpers — PEM conversion, Base64 utils,
 * AES-GCM key creation, RSA key import, and logging.
 */

//
// 🪵 Logging Helper
//
export function cryptoLog(step, data = null, type = "info") {
  const timestamp = new Date().toISOString();
  const prefix = `[Crypto:${step}]`;
  if (type === "error") console.error(prefix, data, `@${timestamp}`);
  else if (type === "warn") console.warn(prefix, data, `@${timestamp}`);
  else console.log(prefix, data, `@${timestamp}`);
}

//
// 🔐 PEM ↔ ArrayBuffer conversion
//
export function convertPemToArrayBuffer(pem) {
  const base64 = pem.replace(/-----.*?-----/g, "").replace(/\s+/g, "");
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

//
// 🔑 RSA Key Import (Public / Private)
//
export async function importRsaPublicKey(pem, hashAlgorithm = "SHA-256") {
  const binaryDer = convertPemToArrayBuffer(pem);
  return await window.crypto.subtle.importKey(
    "spki",
    binaryDer,
    {
      name: "RSA-OAEP",
      hash: { name: hashAlgorithm },
    },
    false,
    ["encrypt"]
  );
}

export async function importRsaPrivateKey(pem, hashAlgorithm = "SHA-256") {
  const binaryDer = convertPemToArrayBuffer(pem);
  return await window.crypto.subtle.importKey(
    "pkcs8",
    binaryDer,
    {
      name: "RSA-OAEP",
      hash: { name: hashAlgorithm },
    },
    false,
    ["decrypt"]
  );
}

//
// 🧬 AES Key Generation (GCM mode)
//
export async function generateAesKey(length = 256) {
  return await window.crypto.subtle.generateKey(
    { name: "AES-GCM", length },
    true,
    ["encrypt", "decrypt"]
  );
}

//
// 🔁 Base64 utilities
//
export function convertBase64ToBytes(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function convertBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}
